import { Mark, mergeAttributes } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

export interface AgentSuggestionAttrs {
    editId: string;
    newString: string;
    rationale?: string;
}

const SVG_NS = "http://www.w3.org/2000/svg";

function buildSvg(
    width: number,
    height: number,
    viewBox: string,
    strokeWidth: string,
    paths: readonly string[]
): SVGElement {
    const svg = document.createElementNS(SVG_NS, "svg");
    svg.setAttribute("width", String(width));
    svg.setAttribute("height", String(height));
    svg.setAttribute("viewBox", viewBox);
    svg.setAttribute("fill", "none");
    svg.setAttribute("stroke", "currentColor");
    svg.setAttribute("stroke-width", strokeWidth);
    svg.setAttribute("stroke-linecap", "round");
    svg.setAttribute("stroke-linejoin", "round");
    for (const d of paths) {
        const path = document.createElementNS(SVG_NS, "path");
        path.setAttribute("d", d);
        svg.appendChild(path);
    }
    return svg;
}

/**
 * Builds the inline widget DOM injected by the ProseMirror decoration plugin.
 * Structure (mirrors editor-suggestions.jsx in the V1 Editor handoff):
 *   .sx-after-wrapper
 *     .sx-after [data-suggestion-accept]           ← green replacement chip
 *       textNode(newString)
 *       button.sx-accept [data-suggestion-accept]  ← inline ✓
 *     button.sx-reject [data-suggestion-reject]    ← hover-revealed ✕
 *     .sx-tooltip (hover-revealed)
 *
 * All accept/reject targets carry data-edit-id so the click delegate in
 * TiptapEditor.handleEditorMouseDown can dispatch the resolution against the
 * @codenexus/react bridge.
 */
function buildSuggestionWidget(editId: string, newString: string, rationale: string): HTMLElement {
    const wrapper = document.createElement("span");
    wrapper.className = "sx-after-wrapper";
    wrapper.setAttribute("data-edit-id", editId);
    wrapper.contentEditable = "false";

    const afterSpan = document.createElement("span");
    afterSpan.className = "sx-after";
    afterSpan.setAttribute("data-suggestion-accept", "true");
    afterSpan.setAttribute("data-edit-id", editId);
    afterSpan.setAttribute("role", "button");
    afterSpan.tabIndex = 0;
    // For deletions (newString === ""), show "Delete" so there's a visible
    // click target — an empty chip would be invisible and unclickable.
    afterSpan.appendChild(document.createTextNode(newString || "Delete"));

    const acceptBtn = document.createElement("button");
    acceptBtn.type = "button";
    acceptBtn.className = "sx-accept";
    acceptBtn.setAttribute("data-suggestion-accept", "true");
    acceptBtn.setAttribute("data-edit-id", editId);
    acceptBtn.setAttribute("aria-label", "Accept suggestion");
    acceptBtn.tabIndex = -1;
    acceptBtn.appendChild(buildSvg(10, 10, "0 0 24 24", "3", ["M20 6L9 17l-5-5"]));
    afterSpan.appendChild(acceptBtn);
    wrapper.appendChild(afterSpan);

    const rejectBtn = document.createElement("button");
    rejectBtn.type = "button";
    rejectBtn.className = "sx-reject";
    rejectBtn.setAttribute("data-suggestion-reject", "true");
    rejectBtn.setAttribute("data-edit-id", editId);
    rejectBtn.setAttribute("aria-label", "Reject suggestion");
    rejectBtn.title = "Discard suggestion";
    rejectBtn.tabIndex = -1;
    rejectBtn.appendChild(buildSvg(9, 9, "0 0 12 12", "2.2", ["M3 3l6 6", "M9 3l-6 6"]));
    wrapper.appendChild(rejectBtn);

    const tooltip = document.createElement("span");
    tooltip.className = "sx-tooltip";
    tooltip.setAttribute("role", "tooltip");
    tooltip.setAttribute("aria-hidden", "true");

    const tipLine = document.createElement("span");
    tipLine.className = "sx-tip-line";
    tipLine.textContent = "Click the green text or ✓ to accept this change.";
    tooltip.appendChild(tipLine);

    if (rationale) {
        const tipRationale = document.createElement("span");
        tipRationale.className = "sx-tip-rationale";
        tipRationale.textContent = rationale;
        tooltip.appendChild(tipRationale);
    }

    const tipArrow = document.createElement("span");
    tipArrow.className = "sx-tip-arrow";
    tooltip.appendChild(tipArrow);
    wrapper.appendChild(tooltip);

    return wrapper;
}

declare module "@tiptap/core" {
    interface Commands<ReturnType> {
        agentSuggestion: {
            stageSuggestion: (attrs: AgentSuggestionAttrs) => ReturnType;
            acceptSuggestion: (editId: string) => ReturnType;
            rejectSuggestion: (editId: string) => ReturnType;
        };
    }
}

/**
 * TipTap mark used by the @codenexus/react host-document bridge to stage an
 * agent-proposed edit inline.
 *
 * Rendering happens in two pieces:
 *   1. The mark itself renders the original text as a red strikethrough
 *      `<span class="sx-suggestion">` (see renderHTML below).
 *   2. A ProseMirror widget decoration injects the green replacement chip,
 *      inline ✓ accept button, hover ✕ reject button, and dark tooltip
 *      immediately after the marked range. The widget DOM and its data-attrs
 *      are picked up by TiptapEditor.handleEditorMouseDown to dispatch
 *      `bridge.adapter.resolve(...)`.
 *
 * Accept/reject commands (acceptSuggestion / rejectSuggestion) mutate the doc
 * accordingly; the widget disappears automatically once the mark is gone.
 */
export const AgentSuggestion = Mark.create({
    name: "agent_suggestion",

    addAttributes() {
        return {
            editId: { default: "" },
            newString: { default: "" },
            rationale: { default: "" }
        };
    },

    parseHTML() {
        return [{ tag: "span[data-agent-suggestion]" }];
    },

    renderHTML({ HTMLAttributes, mark }) {
        // The mark wraps the original text only; the green chip + buttons + tooltip
        // are rendered separately by the widget-decoration plugin defined below.
        return [
            "span",
            mergeAttributes(HTMLAttributes, {
                class: "sx-suggestion",
                "data-agent-suggestion": "true",
                "data-edit-id": mark.attrs.editId,
                "data-new-string": mark.attrs.newString,
                "data-rationale": mark.attrs.rationale,
                title: `Click to accept "${mark.attrs.newString}" · Alt+click to reject${
                    mark.attrs.rationale ? ` · ${mark.attrs.rationale}` : ""
                }`
            }),
            0
        ];
    },

    addProseMirrorPlugins() {
        const markName = this.name;
        return [
            new Plugin({
                key: new PluginKey("agent-suggestion-widgets"),
                props: {
                    decorations(state) {
                        const decorations: Decoration[] = [];
                        const seenEditIds = new Set<string>();
                        state.doc.descendants((node, pos) => {
                            if (!node.isText) return;
                            const mark = node.marks.find(m => m.type.name === markName);
                            if (!mark) return;
                            const editId = String(mark.attrs.editId || "");
                            // One widget per editId. A mark that happens to span multiple
                            // adjacent text nodes would otherwise produce duplicates.
                            if (!editId || seenEditIds.has(editId)) return;
                            seenEditIds.add(editId);
                            const newString = String(mark.attrs.newString ?? "");
                            const rationale = String(mark.attrs.rationale ?? "");
                            const endPos = pos + node.nodeSize;
                            decorations.push(
                                Decoration.widget(
                                    endPos,
                                    () => buildSuggestionWidget(editId, newString, rationale),
                                    { key: `agent-suggestion-${editId}`, side: 1 }
                                )
                            );
                        });
                        return DecorationSet.create(state.doc, decorations);
                    }
                }
            })
        ];
    },

    addCommands() {
        return {
            stageSuggestion:
                attrs =>
                ({ commands }) =>
                    commands.setMark(this.name, attrs),

            acceptSuggestion:
                editId =>
                ({ tr, state, dispatch }) => {
                    // First pass: aggregate the full PM range covered by all text
                    // nodes carrying this mark (a mark may span multiple adjacent
                    // text nodes, e.g. when the range contains bold/italic runs).
                    let markFrom = Infinity;
                    let markTo = -Infinity;
                    let mark: any = null;
                    state.doc.descendants((node, pos) => {
                        const m = node.marks.find(
                            mk => mk.type.name === this.name && mk.attrs.editId === editId
                        );
                        if (!m) return;
                        if (!mark) mark = m;
                        if (pos < markFrom) markFrom = pos;
                        if (pos + node.nodeSize > markTo) markTo = pos + node.nodeSize;
                    });
                    if (markFrom === Infinity || !mark) return false;

                    const newStr = String(mark.attrs.newString ?? "");
                    if (newStr === "") {
                        // Deletion path. schema.text("") would throw "Empty text node",
                        // so we use tr.delete. When the mark covers ALL content of its
                        // parent textblock we delete the whole block node so the
                        // bullet/number disappears too and the list renumbers correctly.
                        const $from = state.doc.resolve(markFrom);
                        const tbDepth = $from.depth; // paragraph is the innermost textblock
                        const blockStart = $from.start(tbDepth);
                        const blockEnd = $from.end(tbDepth);
                        if (markFrom === blockStart && markTo === blockEnd) {
                            // Full block — delete the enclosing list item if present,
                            // otherwise delete the block node itself.
                            let deletedListItem = false;
                            for (let d = tbDepth - 1; d >= 1; d--) {
                                const name = $from.node(d).type.name;
                                if (name === "listItem" || name === "list_item") {
                                    tr.delete($from.before(d), $from.after(d));
                                    deletedListItem = true;
                                    break;
                                }
                            }
                            if (!deletedListItem) {
                                tr.delete($from.before(tbDepth), $from.after(tbDepth));
                            }
                        } else {
                            // Partial deletion within a block
                            tr.delete(markFrom, markTo);
                        }
                    } else {
                        tr.replaceWith(markFrom, markTo, state.schema.text(newStr));
                    }
                    if (dispatch) dispatch(tr);
                    return true;
                },

            rejectSuggestion:
                editId =>
                ({ tr, state, dispatch }) => {
                    let found = false;
                    state.doc.descendants((node, pos) => {
                        const m = node.marks.find(
                            mk => mk.type.name === this.name && mk.attrs.editId === editId
                        );
                        if (!m) return;
                        found = true;
                        tr.removeMark(pos, pos + node.nodeSize, m.type);
                    });
                    if (found && dispatch) dispatch(tr);
                    return found;
                }
        };
    }
});
